import type { SavedViewFilter } from '@/stores/persistentTableStore';
import type { SavedViewDefinitionV2 } from '@/utils/savedViewDefinition';

/**
 * Share-link helpers (SAU-310).
 *
 * This file used to hold a base64 codec that packed an entire view into the
 * query string. It is gone, along with `SHAREABLE_URL_WARN_LENGTH` (6000) and
 * `SHAREABLE_URL_HARD_LIMIT` (12000) — the latter was above CloudFront's
 * 8,192-byte URL ceiling, so the code permitted links that were rejected at the
 * edge and never reached the origin.
 *
 * A link now carries a 12-character share key and nothing else, so it cannot
 * grow with the complexity of the view. Legacy `?d=` links are NOT decoded:
 * their payload sits in the URL where anyone holding it can read the view name
 * and every filter value with no permission check possible. That was the one
 * path with no gate, which is why it was dropped rather than carried.
 */

/** Query parameter carrying a share key. */
export const SHARE_KEY_PARAM = 's';

/** Legacy parameter. Detected so the user gets told why, never decoded. */
export const LEGACY_SHARE_PAYLOAD_PARAM = 'd';

/**
 * Build a full URL the recipient can click. `origin` is normally
 * `window.location.origin`; it is a parameter so unit tests need no DOM.
 *
 * About 63 characters in production, and fixed — the whole point.
 */
/**
 * The share key in whatever someone pasted into "Import a view": a whole share
 * link, or the bare key on its own. Null when there is no key to read.
 */
export function shareKeyFromInput(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === '') {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).searchParams.get(SHARE_KEY_PARAM) || null;
    } catch {
      return null;
    }
  }
  return /\s|\//.test(trimmed) ? null : trimmed;
}

export function buildShareUrl(origin: string, shareKey: string): string {
  // Strip any trailing slash so we never emit `https://app//views`.
  const trimmedOrigin = origin.replace(/\/+$/, '');
  return `${trimmedOrigin}/views/import?${SHARE_KEY_PARAM}=${encodeURIComponent(shareKey)}`;
}

/**
 * Write the share link to the clipboard as both an HTML hyperlink (so pasting
 * into Slack / email / docs renders the view name as the visible link text) and
 * as a plain-text URL fallback (for terminals or any client that ignores HTML).
 *
 * Returns true if the rich-text write succeeded; false if it fell back to a
 * plain-text-only write (older browsers, restrictive contexts). The caller
 * toasts either way — the link works regardless.
 */
export async function copyShareToClipboard(
  viewName: string,
  shareUrl: string | Promise<string>,
): Promise<boolean> {
  const safeName = escapeHtml(viewName);
  // The URL may still be in flight: the share key is minted by the server, and
  // Safari only honours a clipboard write that starts INSIDE the click — it does
  // not carry the gesture across a network await. So the write is started at
  // once with promise-backed blobs, which is the pattern Safari added exactly
  // for "copy something you have to fetch first". The caller must call this
  // synchronously from the click handler for that to hold.
  const resolvedUrl = Promise.resolve(shareUrl);
  const htmlBlob = resolvedUrl.then(
    (url) => new Blob([`<a href="${escapeAttribute(url)}">${safeName}</a>`], { type: 'text/html' }),
  );
  const plainBlob = resolvedUrl.then((url) => new Blob([url], { type: 'text/plain' }));
  // On the fallback path below nothing consumes these two, so a URL that never
  // arrives (the publish failed) would otherwise reject them unobserved — two
  // console errors and two Sentry events on top of the toast that already said
  // the share failed. The caller sees the failure through `resolvedUrl` itself.
  htmlBlob.catch(() => {});
  plainBlob.catch(() => {});

  // Prefer the modern ClipboardItem API so the HTML payload travels alongside
  // the plain-text fallback. Slack picks up the `<a>` and renders the view
  // name as the visible link.
  try {
    if (
      typeof ClipboardItem !== 'undefined'
      && navigator.clipboard
      && 'write' in navigator.clipboard
    ) {
      const item = new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': plainBlob });
      await navigator.clipboard.write([item]);
      return true;
    }
  } catch {
    // Fall through to plain-text path — some browsers reject ClipboardItem
    // in non-secure contexts, without an active user gesture, or with
    // promise-valued entries.
  }

  // Plain-text fallback. `writeText` is far more widely supported. Awaiting the
  // URL here is what loses the gesture on Safari, but Safari takes the path
  // above; this is for browsers that do not have ClipboardItem at all.
  const plain = await resolvedUrl;
  if (navigator.clipboard && 'writeText' in navigator.clipboard) {
    await navigator.clipboard.writeText(plain);
    return false;
  }

  throw new Error('Clipboard write is not supported in this environment.');
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttribute(input: string): string {
  // Attributes need the same escapes plus newline normalisation.
  return escapeHtml(input).replace(/\r?\n/g, '');
}

/** Cheap summary helper for the import confirmation dialog. */
export function countRulesInFilter(filter: SavedViewFilter): number {
  function walk(node: unknown): number {
    if (!node || typeof node !== 'object') {
      return 0;
    }
    const bag = node as { rules?: unknown[]; column?: unknown };
    if ('column' in bag && bag.column !== undefined) {
      // Leaf rule.
      return 1;
    }
    if (Array.isArray(bag.rules)) {
      return bag.rules.reduce<number>((sum, child) => sum + walk(child), 0);
    }
    return 0;
  }
  return walk(filter.ruleScopes);
}

/**
 * How many columns a stored definition shows. Reads the definition's own
 * ordered visible list rather than counting falsy entries in a hidden map,
 * because a pruned definition has no hidden map to count.
 */
export function countVisibleColumns(definition: SavedViewDefinitionV2): number {
  return definition.columns?.length ?? 0;
}
